import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingCart, Star, ChevronLeft, ShieldCheck, Users, 
  Smartphone, Zap, Lock, Download, Mail, Info, Heart, ShieldAlert 
} from 'lucide-react';
import { productApi, settingsApi } from '@/api-client';
import type { Product } from '@/types/product';
import { getPriceStr } from '@/types/product';
import { SEO } from '@/components/SEO';

export function ProductDetail({ onAddToCart }: { onAddToCart: (p: Product, q: number) => void }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<any>(null);
  const [related, setRelated] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  
  // ═══ SHOP STATUS – GILT FÜR ALLE PRODUKTE ═══
  const [shopEnabled, setShopEnabled] = useState(true);
  
  // ═══ AGE GATE ═══
  const [ageVerified, setAgeVerified] = useState(() => {
    try { return localStorage.getItem('jl-age-verified') === 'true'; } catch { return false; }
  });
  const [showAdultGate, setShowAdultGate] = useState(false);
  const [productLoaded, setProductLoaded] = useState(false);

  useEffect(() => {
    settingsApi.getShopStatus()
      .then(d => setShopEnabled(d.enabled))
      .catch(() => setShopEnabled(false));
  }, []);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    productApi.get(id)
      .then(data => {
        if (data && typeof data === 'object') {
          setProduct(data);
          setProductLoaded(true);
          
          // ═══ SOFORTIGE ALTERSPRÜFUNG BEI 18+ PRODUKTEN ═══
          if ((data as any).isAdult === true && !ageVerified) {
            setShowAdultGate(true);
          }
          
          productApi.list().then(list => {
            if (Array.isArray(list)) {
              setRelated(list.filter(p => p.id !== data.id && !(p as any).isAdult).slice(0, 3));
            }
          }).catch(err => console.error("Fehler beim Laden verwandter Produkte:", err));
        } else {
          setProduct(null);
          setProductLoaded(true);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("API Fehler in ProductDetail:", err);
        setProduct(null);
        setProductLoaded(true);
        setLoading(false);
      });
  }, [id, ageVerified]);

  const confirmAge = () => {
    try { localStorage.setItem('jl-age-verified', 'true'); } catch {}
    setAgeVerified(true);
    setShowAdultGate(false);
  };

  const rejectAge = () => {
    setShowAdultGate(false);
    navigate('/shop');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-surface-primary">
        <div className="animate-spin w-10 h-10 border-2 border-gold border-t-transparent rounded-full mb-4" />
        <p className="text-text-muted animate-pulse">Produkt-Details werden geladen...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-surface-primary px-6 text-center">
        <h2 className="text-2xl font-bold text-text-primary mb-4">Produkt nicht gefunden</h2>
        <p className="text-text-secondary mb-8">Das gesuchte Produkt existiert leider nicht oder wurde verschoben.</p>
        <Link to="/shop" className="px-6 py-3 bg-gold text-surface-primary font-bold rounded-xl shadow-gold transition-all">
          Zurück zum Shop
        </Link>
      </div>
    );
  }

  const isLustDuell = product.name?.toLowerCase().includes('lustduell') || product.slug?.includes('lustduell');
  const descriptionText = product.longDescription || product.description || '';
  const isAdult = (product as any).isAdult === true;

  // ═══ JUGENDSCHUTZ: Wenn 18+ und nicht freigegeben, zeige nur das Modal ═══
  if (isAdult && !ageVerified) {
    return (
      <div className="min-h-screen bg-surface-primary flex items-center justify-center px-4">
        <AnimatePresence>
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-surface border border-crimson/30 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
          >
            <ShieldAlert size={48} className="text-crimson mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-text-primary mb-2">Altersbeschränkung</h2>
            <p className="text-text-secondary mb-6 text-sm leading-relaxed">
              Dieses Produkt enthält Inhalte, die nur für Erwachsene ab 18 Jahren bestimmt sind. 
              Mit dem Betritt bestätigen Sie, dass Sie das 18. Lebensjahr vollendet haben.
            </p>
            <div className="flex gap-3 justify-center">
              <button 
                onClick={() => navigate('/shop')}
                className="px-5 py-2.5 text-text-secondary font-medium rounded-xl hover:bg-white/[0.04] transition-all border border-white/[0.06]"
              >
                Zurück zum Shop
              </button>
              <button 
                onClick={confirmAge}
                className="px-5 py-2.5 bg-crimson text-white font-bold rounded-xl hover:bg-crimson/80 transition-all"
              >
                Ja, ich bin über 18
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={product.name || 'Produktdetails'} 
        description={product.description || ''}
        keywords={isLustDuell ? "LustDuell, Erotik-Brettspiel, Paar App Android, Swinger Spiel digital, Erotik Software diskret" : ""}
        path={`/shop/${product.slug || product.id}`}
      />

      <div className="mt-[70px] bg-surface-primary min-h-screen pb-20">
        <nav className="max-w-[1200px] mx-auto px-6 py-6">
          <Link to="/shop" className="inline-flex items-center gap-2 text-text-muted hover:text-gold transition-colors text-sm font-medium group">
            <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Zurück zum App-Shop
          </Link>
        </nav>

        <article className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            
            <section>
              <motion.div 
                initial={{ opacity: 0, y: 10 }} 
                animate={{ opacity: 1, y: 0 }}
                className="aspect-video rounded-3xl overflow-hidden bg-surface-secondary border border-white/[0.06] mb-4 shadow-2xl relative"
              >
                <img 
                  src={product.screenshots?.[activeScreenshot] || product.image} 
                  alt={`${product.name} Ansicht`} 
                  className="w-full h-full object-cover"
                />
                {product.isAdult && (
                  <span className="absolute top-4 left-4 px-3 py-1 bg-crimson text-white text-[10px] font-bold rounded-full">18+</span>
                )}
              </motion.div>
              
              {product.screenshots && product.screenshots.length > 1 && (
                <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
                  {product.screenshots.map((s: string, i: number) => (
                    <button 
                      key={i} 
                      onClick={() => setActiveScreenshot(i)}
                      className={`w-24 h-16 rounded-xl border-2 transition-all shrink-0 overflow-hidden ${activeScreenshot === i ? 'border-gold' : 'border-transparent opacity-50 hover:opacity-100'}`}
                    >
                      <img src={s} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="flex flex-col">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-gold-dim text-gold border border-gold/20 mb-4">
                  {product.category === 'digital' ? 'Lifestyle-Spiel' : 'Premium Software'}
                </span>
                <h1 className="text-3xl md:text-5xl font-display font-bold italic text-glow leading-tight mb-4 text-text-primary">
                  {product.name}
                </h1>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-1 text-gold">
                    <Star size={16} fill="currentColor" />
                    <span className="ml-2 text-sm font-bold text-text-primary">4.9</span>
                    <span className="text-text-muted text-xs ml-1">(Verifizierter Kauf)</span>
                  </div>
                  <span className="w-px h-4 bg-white/10" />
                  <span className="text-sm text-text-muted flex items-center gap-1.5">
                    <ShieldCheck size={16} className="text-green-500" /> Made in Germany
                  </span>
                </div>
              </div>

              <div className="bg-surface-secondary/50 border border-white/[0.06] rounded-3xl p-8 mb-8">
                <div className="flex items-end gap-3 mb-8">
                  <span className="text-4xl font-black text-text-primary">
                    {getPriceStr(product.price)} <span className="text-lg font-normal text-text-muted">€</span>
                  </span>
                </div>

                <div className="flex flex-wrap gap-4 mb-8">
                  {shopEnabled ? (
                    <button 
                      onClick={() => onAddToCart(product, quantity)}
                      className="flex-1 min-w-[200px] flex items-center justify-center gap-3 px-8 py-4 bg-gold text-surface-primary rounded-2xl font-bold text-lg shadow-gold hover:shadow-gold-lg hover:-translate-y-1 transition-all"
                    >
                      <ShoppingCart size={22} /> In den Warenkorb
                    </button>
                  ) : (
                    <div className="flex-1 min-w-[200px] px-8 py-4 bg-white/[0.03] border border-white/[0.06] rounded-2xl text-text-muted font-bold text-lg text-center">
                      Shop ist im Browsing-Modus
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-xs text-text-secondary">
                    <Download size={14} className="text-gold" /> Sofort-Download
                  </div>
                  <div className="flex items-center gap-2 text-xs text-text-secondary">
                    <Lock size={14} className="text-gold" /> Diskrete Zahlung
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-bold text-text-primary uppercase tracking-widest">Top Features</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <li className="flex items-center gap-2 text-sm text-text-secondary">
                    <Zap size={16} className="text-gold" /> {isLustDuell ? '4-Phasen-Gameplay' : 'Premium Performance'}
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-secondary">
                    <Smartphone size={16} className="text-gold" /> 100% Offline-fähig
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-secondary">
                    <Users size={16} className="text-gold" /> Paar- & Gruppenmodus
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-secondary">
                    <Heart size={16} className="text-gold" /> {isLustDuell ? 'Consent-System' : 'Lifestyle-Fokus'}
                  </li>
                </ul>
              </div>
            </section>
          </div>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-12 pt-16 border-t border-white/[0.06]">
            <div className="lg:col-span-2 prose prose-invert max-w-none">
              <h2 className="text-2xl font-bold text-text-primary mb-6 italic">Produktbeschreibung</h2>
              <div className="text-text-secondary leading-relaxed space-y-6 text-lg">
                {descriptionText ? (
                  descriptionText.split('\n').filter((para: string) => para.trim() !== '').map((para: string, i: number) => (
                    <p key={i}>{para}</p>
                  ))
                ) : (
                  <p>Keine Beschreibung verfügbar.</p>
                )}
              </div>

              {isLustDuell && (
                <div className="mt-12 p-8 rounded-3xl bg-gold-dim border border-gold/10">
                  <h3 className="text-xl font-bold text-gold mb-4 italic">Exklusiv in LustDuell</h3>
                  <p className="text-text-secondary">
                    Profitieren Sie von unserem integrierten <strong>Task-Editor</strong>. Passen Sie Aufgaben an Ihre Vorlieben an oder erstellen Sie völlig neue Challenges für unendliche Wiederspielbarkeit.
                  </p>
                </div>
              )}
            </div>

            <aside className="space-y-8">
              <div className="p-6 rounded-3xl border border-white/[0.06] bg-surface-secondary/50">
                <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
                  <Info size={18} className="text-gold" /> Spezifikationen
                </h3>
                <dl className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <dt className="text-text-muted">Plattform</dt>
                    <dd className="text-text-primary font-medium">Android / Web</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-text-muted">Alter</dt>
                    <dd className="text-text-primary font-medium">{product.isAdult ? 'Ab 18 Jahren' : 'Ab 0 Jahren'}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-text-muted">Sprache</dt>
                    <dd className="text-text-primary font-medium">Deutsch</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-text-muted">Lizenz</dt>
                    <dd className="text-text-primary font-medium">Lebenslange Nutzung</dd>
                  </div>
                </dl>
              </div>

              <div className="p-6 rounded-3xl border border-gold/20 bg-surface-primary">
                <h3 className="text-gold font-bold mb-2">Support</h3>
                <p className="text-xs text-text-secondary mb-4">Direkter Draht zum Entwickler bei Fragen zur Installation.</p>
                <a href="mailto:support@jls-software.de" className="text-sm font-bold text-text-primary hover:text-gold flex items-center gap-2 transition-colors">
                  <Mail size={14} /> Hilfe anfordern
                </a>
              </div>
            </aside>
          </section>

          {related.length > 0 && (
            <section className="py-20 border-t border-white/[0.06] mt-20">
              <h2 className="text-2xl font-bold text-text-primary mb-10 italic text-center">Passend dazu</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {related.map(p => (
                  <Link key={p.id} to={`/shop/${p.slug || p.id}`} className="group block bg-surface-secondary/30 rounded-2xl p-4 border border-transparent hover:border-gold/20 transition-all">
                    <div className="aspect-video rounded-xl overflow-hidden mb-4">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                    <h4 className="font-bold text-text-primary group-hover:text-gold transition-colors">{p.name}</h4>
                    <p className="text-sm text-gold mt-1 font-bold">{getPriceStr(p.price)} €</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </div>
    </>
  );
}